import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Metrics } from '../../../types';

export type MaterializationWindowRegion = {
  start: number;
  end: number;
  isAnomalous: boolean;
  isOngoing: boolean;
  startTime: string;
  restoreTime: string | null;
};

const useChartMaterialization = ({
  metrics,
  dimensionValue,
}: {
  metrics?: Metrics[];
  dimensionValue?: string;
}): MaterializationWindowRegion[] =>
  useMemo(() => {
    const regions: MaterializationWindowRegion[] = [];
    const relevantMetrics = (metrics || [])
      .filter((m) => !dimensionValue || m.dimension_value === dimensionValue)
      .slice()
      .sort(
        (a, b) =>
          dayjs.utc(a.end_time).valueOf() - dayjs.utc(b.end_time).valueOf(),
      );

    let run: Metrics[] = [];

    const flushRun = (nextMetric: Metrics | undefined) => {
      if (!run.length) return;

      const first = run[0];
      const last = run[run.length - 1];
      const until = last.materialization_window_until;

      if (until && first.end_time) {
        // The window's own zero point(s) carry its anomaly verdict (both for
        // a trailing window and for a slow paired one) - the restore point's
        // own is_anomalous is left alone, so its dot reflects only its own
        // (usually in-band) value.
        regions.push({
          start: dayjs.utc(first.end_time).valueOf(),
          end: dayjs.utc(until).valueOf(),
          isAnomalous: run.some((item) => item.is_anomalous === true),
          isOngoing: !nextMetric,
          startTime: first.end_time,
          restoreTime: nextMetric ? nextMetric.end_time ?? null : null,
        });
      }
      run = [];
    };

    relevantMetrics.forEach((item, index) => {
      if (item.is_materialization_window) {
        run.push(item);
      } else {
        flushRun(item);
      }
      if (
        index === relevantMetrics.length - 1 &&
        item.is_materialization_window
      ) {
        flushRun(undefined);
      }
    });

    return regions;
  }, [metrics, dimensionValue]);

export default useChartMaterialization;
